import { SkeletonLoader } from '@toggle/design-system';
import { useScrollListener } from '@toggle/helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AlertsCard } from '~/modules/alerts-card/AlertsCard';
import { usePostAlerts } from '~/react-query-hooks/events/use-event-alerts/useEventAlerts';

import { ErrorMessageWrapper } from '../error-message-wrapper/ErrorMessageWrapper';
import styles from './AlertsDropdown.module.scss';
import { Footer } from './components/footer/Footer';
import { Header } from './components/header/Header';
import { EmptyState } from './modules/empty-state/EmptyState';

interface AlertsDropdownProps {
  onClose: () => void;
}

export const AlertsDropdown = ({ onClose }: AlertsDropdownProps) => {
  useTranslation('alerts');
  const [activeButtonIndex, setActiveButtonIndex] = useState(0);
  const isUnreadTab = activeButtonIndex === 1;
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  // Fetch unread count to display in header
  const { data: unreadCountData } = usePostAlerts({ count_only: true });
  const unreadCount = unreadCountData?.pages[0]?.unread_count ?? 0;

  const {
    isError,
    isLoading,
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePostAlerts({
    limit: 10,
    ...(isUnreadTab && { read: false }),
  });

  const alerts = (data?.pages?.flatMap(page => page.alerts) ?? []).filter(
    alert => (isUnreadTab ? !alert.read : true)
  );

  useEffect(() => {
    const currentContentRef = contentRef.current;

    if (!currentContentRef) {
      return;
    }

    setHasOverflow(
      currentContentRef.scrollHeight > currentContentRef.clientHeight
    );
  }, [isLoading, activeButtonIndex, alerts.length]);

  useScrollListener(
    contentRef,
    () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }

      return hasNextPage;
    },
    [hasNextPage, isFetchingNextPage],
    { isGlobalScrollListened: false, offset: 120 }
  );

  const showEmptyState = isUnreadTab && unreadCount === 0 && !isLoading;
  const showFooter = !showEmptyState && !isError && !isLoading && !hasNextPage;

  const handleButtonClick = (index: number) => setActiveButtonIndex(index);

  const getContent = () => {
    if (isLoading) {
      return (
        <div className={styles.skeletonLoaderWrapper}>
          <SkeletonLoader
            areas={[
              {
                styles: { width: '100%', height: '100%', borderRadius: '8px' },
              },
            ]}
          />
        </div>
      );
    }

    if (isError) {
      return <ErrorMessageWrapper showBorder={false} displayIcon />;
    }

    if (showEmptyState) {
      return <EmptyState onCloseDropdown={onClose} />;
    }

    return (
      <>
        {alerts.map(alert => (
          <AlertsCard key={alert.id} alertEvent={alert} />
        ))}
        {hasNextPage && isFetchingNextPage && (
          <div className={styles.paginationLoaderWrapper}>
            <SkeletonLoader
              areas={[
                {
                  styles: {
                    width: '100%',
                    height: '60px',
                    borderRadius: '8px',
                  },
                },
              ]}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.dropdownContainer}>
      <Header
        activeButtonIndex={activeButtonIndex}
        onButtonClick={handleButtonClick}
        onClose={onClose}
      />
      <div className={styles.contentWrapper} ref={contentRef}>
        {getContent()}
        {showFooter && <Footer hasScroll={hasOverflow} onClose={onClose} />}
      </div>
    </div>
  );
};
